import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { TextArea } from '../../ui/components/TextArea';
import { Badge } from '../../ui/components/Badge';
import {
  FeatherX,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherCheck,
  FeatherImage,
  FeatherPlus,
  FeatherTrash2,
  FeatherShield,
  FeatherAlertCircle,
} from '@subframe/core';
import { mutateNFT, requestMinterAuthorization, type AlternateImageInput, type MutationResult } from '../../api/mutations';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import { toSigningPayload } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface NFTData {
  nft_token_id: string;
  name: string;
  description?: string;
  image_url?: string;
  media_url?: string;
  thumbnail_url?: string;
  traits?: Array<{ trait_type: string; value: string | number }>;
  traits_with_rarity?: Array<{ trait_type: string; value: string | number; rarity_percentage?: number }>;
  is_mutable?: boolean;
  metadata_version?: number;
  /** Full metadata JSON — we read `properties.files` from here to render alternates. */
  metadata?: {
    image?: string;
    properties?: {
      files?: Array<{ uri?: string; type?: string; cdn_url?: string }>;
    };
  } & Record<string, unknown>;
}

/**
 * One alternate image tile rendered in the modal, whether already on-chain or newly
 * added in this session. `kind=keep` stores the authoritative IPFS URI; `kind=new`
 * holds the File + a blob preview URL to revoke on cleanup.
 */
type AlternateSlot =
  | { kind: 'keep'; uri: string; mime_type?: string; displayUrl: string }
  | { kind: 'new'; file: File; previewUrl: string; mime_type?: string };

interface MutateNFTModalProps {
  open: boolean;
  onClose: () => void;
  nft: NFTData;
  /** Whether the current user is the NFT issuer (vs a project manager) */
  isIssuer: boolean;
  onSuccess: (result: MutationResult) => void;
  onAuthorizationRequired: (requiredMinter: string) => void;
}

type Step = 'edit' | 'preview' | 'confirm' | 'authorize' | 'sign';

export const MutateNFTModal: React.FC<MutateNFTModalProps> = ({
  open,
  onClose,
  nft,
  isIssuer,
  onSuccess,
  onAuthorizationRequired,
}) => {
  const [step, setStep] = useState<Step>('edit');
  const [name, setName] = useState(nft.name || '');
  const [description, setDescription] = useState(nft.description || '');
  const [traits, setTraits] = useState<Array<{ trait_type: string; value: string }>>(
    () => {
      const source = nft.traits_with_rarity || nft.traits || [];
      return source.map(t => ({ trait_type: t.trait_type, value: String(t.value) }));
    }
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [alternates, setAlternates] = useState<AlternateSlot[]>([]);
  /** Snapshot of the `keep`-kind alternates when the modal opened, used to compute removed/added in the preview diff. */
  const [initialAlternates, setInitialAlternates] = useState<Array<Extract<AlternateSlot, { kind: 'keep' }>>>([]);
  /** Null until user interacts with alternates in this session. Null = leave properties.files alone. */
  const [alternatesTouched, setAlternatesTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const altFilesRef = useRef<HTMLInputElement>(null);

  const MAX_ALTERNATES = 10;

  // Authorization state (non-issuer path: issuer grants our minter AccountSet)
  const [authorizingMinter, setAuthorizingMinter] = useState(false);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);
  const [authorizationSuccess, setAuthorizationSuccess] = useState(false);

  // User-signed mutation state (issuer path: user signs NFTokenModify directly)
  const [signPayload, setSignPayload] = useState<SigningPayload | null>(null);
  const [pendingResult, setPendingResult] = useState<MutationResult | null>(null);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setStep('edit');
      setName(nft.name || '');
      setDescription(nft.description || '');
      const source = nft.traits_with_rarity || nft.traits || [];
      setTraits(source.map(t => ({ trait_type: t.trait_type, value: String(t.value) })));
      setImageFile(null);
      setImagePreview(null);
      setSubmitting(false);
      setError(null);
      setAuthorizingMinter(false);
      setXamanPayload(null);
      setAuthorizationSuccess(false);
      setSignPayload(null);
      setPendingResult(null);

      // Seed alternates from nft.metadata.properties.files, skipping index 0 (primary).
      // Each alternate is a Metaplex-style `{uri, type, cdn_url?}` entry; we prefer
      // cdn_url for display but keep the canonical ipfs:// uri as the source of truth.
      const files = nft.metadata?.properties?.files;
      const primaryImage = nft.metadata?.image;
      const seeded: Array<Extract<AlternateSlot, { kind: 'keep' }>> = [];
      if (Array.isArray(files)) {
        for (const f of files) {
          if (!f || typeof f !== 'object' || typeof f.uri !== 'string') continue;
          if (f.uri === primaryImage) continue; // skip the primary
          const display = f.cdn_url || (f.uri.startsWith('ipfs://')
            ? `https://ipfs.io/ipfs/${f.uri.slice('ipfs://'.length)}`
            : f.uri);
          seeded.push({ kind: 'keep', uri: f.uri, mime_type: f.type, displayUrl: display });
        }
      }
      setAlternates(seeded);
      setInitialAlternates(seeded);
      setAlternatesTouched(false);
    }
  }, [open, nft]);

  // Revoke object URLs created for new-alternate previews when they leave state
  React.useEffect(() => {
    return () => {
      for (const a of alternates) {
        if (a.kind === 'new') URL.revokeObjectURL(a.previewUrl);
      }
    };
    // We intentionally don't include `alternates` — we only want cleanup on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (file.size > 50 * 1024 * 1024) {
      setError('Image must be 50MB or less');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.glb')) {
      setError('Supported formats: PNG, JPEG, GIF, WebP, GLB');
      return;
    }

    setImageFile(file);
    setError(null);

    // Create preview URL
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImagePreview(url);
    } else {
      setImagePreview(null); // GLB has no preview
    }
  }, []);

  const handleAddAlternates = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    const toAdd: AlternateSlot[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 15 * 1024 * 1024) {
        setError(`"${file.name}" exceeds the 15MB alternate image limit`);
        continue;
      }
      if (!validTypes.includes(file.type)) {
        setError(`"${file.name}" is not a supported format (PNG, JPEG, GIF, WebP)`);
        continue;
      }
      toAdd.push({
        kind: 'new',
        file,
        previewUrl: URL.createObjectURL(file),
        mime_type: file.type || undefined,
      });
    }

    if (toAdd.length === 0) {
      // Clear the input so the same file can be re-chosen after a validation fail
      if (altFilesRef.current) altFilesRef.current.value = '';
      return;
    }

    setAlternates(prev => {
      const room = MAX_ALTERNATES - prev.length;
      if (toAdd.length > room) {
        setError(`At most ${MAX_ALTERNATES} alternate images allowed. ${toAdd.length - room} ignored.`);
      }
      return [...prev, ...toAdd.slice(0, room)];
    });
    setAlternatesTouched(true);
    if (altFilesRef.current) altFilesRef.current.value = '';
  }, []);

  const handleRemoveAlternate = (index: number) => {
    setAlternates(prev => {
      const removed = prev[index];
      if (removed && removed.kind === 'new') URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setAlternatesTouched(true);
  };

  const handleAddTrait = () => {
    setTraits(prev => [...prev, { trait_type: '', value: '' }]);
  };

  const handleRemoveTrait = (index: number) => {
    setTraits(prev => prev.filter((_, i) => i !== index));
  };

  const handleTraitChange = (index: number, field: 'trait_type' | 'value', value: string) => {
    setTraits(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  // Compute what changed between original and edited
  const getChanges = () => {
    const changes: string[] = [];
    if (name.trim() !== (nft.name || '')) changes.push('Name');
    if (description.trim() !== (nft.description || '')) changes.push('Description');
    if (imageFile) changes.push('Image');

    const originalTraits = (nft.traits_with_rarity || nft.traits || []).map(t => `${t.trait_type}:${String(t.value)}`).sort();
    const newTraits = traits.filter(t => t.trait_type.trim() && t.value.trim()).map(t => `${t.trait_type}:${t.value}`).sort();
    if (JSON.stringify(originalTraits) !== JSON.stringify(newTraits)) changes.push('Traits');

    if (alternatesTouched) changes.push('Alternate images');

    return changes;
  };

  const changes = getChanges();
  const hasChanges = changes.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const validTraits = traits.filter(t => t.trait_type.trim() && t.value.trim());

      const alternatesPayload: AlternateImageInput[] | undefined = alternatesTouched
        ? alternates.map(a =>
            a.kind === 'keep'
              ? ({ kind: 'keep', uri: a.uri, mime_type: a.mime_type } as AlternateImageInput)
              : ({ kind: 'new', file: a.file, mime_type: a.mime_type } as AlternateImageInput),
          )
        : undefined;

      const result = await mutateNFT(nft.nft_token_id, {
        name: name.trim(),
        description: description.trim() || undefined,
        traits: validTraits.length > 0 ? validTraits : undefined,
        image: imageFile || undefined,
        alternates: alternatesPayload,
      });

      if (!result.success) {
        // Check for authorization requirement — switch to authorize step
        if (result.error === 'NFTokenMinter authorization required' && (result as any).data?.requiredMinter) {
          setStep('authorize');
          setSubmitting(false);
          return;
        }
        throw new Error(result.error || 'Mutation failed');
      }

      if (result.data?.mode === 'user-signed' && (result.data.signingIntent || result.data.payload)) {
        // Issuer signs the NFTokenModify themselves — show Xaman signing UI.
        setPendingResult(result.data);
        setSignPayload(toSigningPayload(result.data.signingIntent ?? result.data.payload));
        setStep('sign');
        setSubmitting(false);
        return;
      }

      if (result.data) {
        onSuccess(result.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mutate NFT');
      setSubmitting(false);
    }
  };

  const handleSignSuccess = (txid: string) => {
    setSignPayload(null);
    const merged: MutationResult = {
      ...(pendingResult || {}),
      transactionHash: txid,
    };
    setPendingResult(null);
    onSuccess(merged);
  };

  const handleSignRejected = () => {
    setSignPayload(null);
    setError('Signing was rejected. Your mutation has not been submitted.');
    setStep('confirm');
  };

  const handleSignExpired = () => {
    setSignPayload(null);
    setError('Signing request expired. Please try again.');
    setStep('confirm');
  };

  const handleRequestAuthorization = async () => {
    setAuthorizingMinter(true);
    setError(null);

    try {
      const result = await requestMinterAuthorization(nft.nft_token_id);

      if (!result.success || !(result.data?.signingIntent || result.data?.payload)) {
        throw new Error(result.error || 'Failed to create authorization request');
      }

      setXamanPayload(toSigningPayload(result.data.signingIntent ?? result.data.payload));
      setAuthorizingMinter(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create authorization request');
      setAuthorizingMinter(false);
    }
  };

  const handleAuthorizationSuccess = (_txid: string) => {
    setXamanPayload(null);
    setAuthorizingMinter(false);
    setAuthorizationSuccess(true);
  };

  const handleAuthorizationRejected = () => {
    setXamanPayload(null);
    setAuthorizingMinter(false);
    setError('Authorization was rejected. You need to authorize the minter to proceed.');
  };

  const handleAuthorizationExpired = () => {
    setXamanPayload(null);
    setAuthorizingMinter(false);
    setError('Authorization request expired. Please try again.');
  };

  const handleRetryAfterAuth = () => {
    setStep('confirm');
    setAuthorizationSuccess(false);
    setError(null);
  };

  if (!open) return null;

  const currentImageUrl = nft.thumbnail_url || nft.media_url || nft.image_url;
  const isBusy = submitting || authorizingMinter;

  const getStepLabel = () => {
    switch (step) {
      case 'edit': return 'Step 1: Edit';
      case 'preview': return 'Step 2: Preview';
      case 'confirm': return 'Step 3: Confirm';
      case 'authorize': return 'Authorization Required';
      case 'sign': return 'Sign in Xaman';
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { if (!isBusy) onClose(); }}>
        <div className="flex w-full max-w-2xl flex-col rounded-xl border border-neutral-border bg-default-background shadow-2xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-border px-6 py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-heading-3 font-heading-3 text-default-font">Mutate NFT</h2>
              <Badge variant={step === 'authorize' ? 'warning' : 'neutral'}>
                {getStepLabel()}
              </Badge>
            </div>
            {!isBusy && (
              <button onClick={onClose} className="text-subtext-color hover:text-default-font transition-colors">
                <FeatherX className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 rounded-lg border border-error-200 bg-error-50 p-3 text-body font-body text-error-700">
                {error}
              </div>
            )}

            {step === 'edit' && (
              <div className="flex flex-col gap-6">
                {/* Image upload */}
                <div className="flex flex-col gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">Image</span>
                  <div className="flex items-start gap-4">
                    {/* Current/New image preview */}
                    <div className="relative h-32 w-32 flex-shrink-0 overflow-hidden rounded-lg border border-neutral-border bg-neutral-50">
                      {(imagePreview || currentImageUrl) ? (
                        <img
                          src={imagePreview || currentImageUrl}
                          alt="NFT preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <FeatherImage className="h-8 w-8 text-subtext-color" />
                        </div>
                      )}
                      {imageFile && (
                        <div className="absolute top-1 right-1">
                          <Badge variant="success">New</Badge>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {imageFile ? 'Change Image' : 'Upload New Image'}
                      </Button>
                      {imageFile && (
                        <button
                          onClick={() => { setImageFile(null); setImagePreview(null); }}
                          className="text-caption font-caption text-subtext-color hover:text-error-600"
                        >
                          Remove new image
                        </button>
                      )}
                      <p className="text-caption font-caption text-subtext-color">
                        PNG, JPEG, GIF, WebP, or GLB. Max 50MB.
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,.glb"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                  </div>
                </div>

                {/* Alternate images (properties.files) */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Additional images
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {alternates.length} / {MAX_ALTERNATES}
                    </span>
                  </div>
                  <p className="text-caption font-caption text-subtext-color">
                    Shown as a carousel on the NFT page. Remove existing images or add new ones below (PNG, JPEG, GIF, WebP; max 15MB each).
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {alternates.map((alt, index) => (
                      <div
                        key={alt.kind === 'keep' ? `k-${alt.uri}` : `n-${alt.previewUrl}`}
                        className="relative h-24 w-24 overflow-hidden rounded-md border border-neutral-border bg-neutral-50"
                      >
                        <img
                          src={alt.kind === 'keep' ? alt.displayUrl : alt.previewUrl}
                          alt={`Alternate ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {alt.kind === 'new' && (
                          <div className="absolute top-1 left-1">
                            <Badge variant="success">New</Badge>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveAlternate(index)}
                          className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white hover:bg-error-600 transition-colors"
                          aria-label={`Remove alternate image ${index + 1}`}
                        >
                          <FeatherX className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {alternates.length < MAX_ALTERNATES && (
                      <button
                        type="button"
                        onClick={() => altFilesRef.current?.click()}
                        className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-neutral-border bg-neutral-50 text-subtext-color hover:border-brand-400 hover:text-brand-600 transition-colors"
                      >
                        <FeatherPlus className="h-4 w-4" />
                        <span className="text-caption font-caption">Add</span>
                      </button>
                    )}
                  </div>
                  <input
                    ref={altFilesRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleAddAlternates}
                  />
                </div>

                {/* Name */}
                <TextField label="Name">
                  <TextField.Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="NFT name"
                    maxLength={255}
                  />
                </TextField>

                {/* Description */}
                <TextArea label="Description">
                  <TextArea.Input
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    placeholder="NFT description (optional)"
                    maxLength={5000}
                    rows={3}
                  />
                </TextArea>

                {/* Traits */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold font-body-bold text-default-font">Traits</span>
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherPlus className="h-3.5 w-3.5" />}
                      onClick={handleAddTrait}
                    >
                      Add Trait
                    </Button>
                  </div>
                  {traits.length === 0 ? (
                    <p className="text-caption font-caption text-subtext-color">No traits defined.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {traits.map((trait, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={trait.trait_type}
                            onChange={(e) => handleTraitChange(index, 'trait_type', e.target.value)}
                            placeholder="Trait name"
                            className="flex-1 rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-body font-body text-default-font placeholder:text-subtext-color focus:border-brand-600 focus:outline-none"
                          />
                          <input
                            type="text"
                            value={trait.value}
                            onChange={(e) => handleTraitChange(index, 'value', e.target.value)}
                            placeholder="Value"
                            className="flex-1 rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-body font-body text-default-font placeholder:text-subtext-color focus:border-brand-600 focus:outline-none"
                          />
                          <button
                            onClick={() => handleRemoveTrait(index)}
                            className="text-subtext-color hover:text-error-600 transition-colors"
                          >
                            <FeatherTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="flex flex-col gap-6">
                <p className="text-body font-body text-subtext-color">
                  Review the changes before submitting.
                </p>

                {/* Side-by-side comparison — only rendered when the primary image,
                    name, or description actually changed. Traits and alternate-image
                    changes have their own detail sections below, so duplicating the
                    unchanged primary card would just be noise. */}
                {(changes.includes('Name') || changes.includes('Description') || changes.includes('Image')) && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Current */}
                    <div className="flex flex-col gap-3 rounded-lg border border-neutral-border p-4">
                      <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">Current</span>
                      {currentImageUrl && (
                        <img src={currentImageUrl} alt="Current" className="w-full rounded-lg object-cover aspect-square" />
                      )}
                      <div>
                        <span className="text-body-bold font-body-bold text-default-font">{nft.name}</span>
                        {nft.description && (
                          <p className="text-caption font-caption text-subtext-color mt-1 line-clamp-3">{nft.description}</p>
                        )}
                      </div>
                    </div>

                    {/* New */}
                    <div className="flex flex-col gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4">
                      <span className="text-caption-bold font-caption-bold text-brand-700 uppercase tracking-wider">New</span>
                      {(imagePreview || currentImageUrl) && (
                        <img src={imagePreview || currentImageUrl} alt="New" className="w-full rounded-lg object-cover aspect-square" />
                      )}
                      <div>
                        <span className={`text-body-bold font-body-bold ${name.trim() !== (nft.name || '') ? 'text-brand-700' : 'text-default-font'}`}>
                          {name}
                        </span>
                        {description && (
                          <p className={`text-caption font-caption mt-1 line-clamp-3 ${description.trim() !== (nft.description || '') ? 'text-brand-700' : 'text-subtext-color'}`}>
                            {description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Changes summary */}
                <div className="rounded-lg border border-neutral-border p-4">
                  <span className="text-body-bold font-body-bold text-default-font">Changes</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {changes.map(change => (
                      <Badge key={change} variant="brand">{change}</Badge>
                    ))}
                    {!hasChanges && (
                      <span className="text-caption font-caption text-warning-600">No changes detected</span>
                    )}
                  </div>
                </div>

                {/* Alternate-images diff */}
                {changes.includes('Alternate images') && (() => {
                  const keptUris = new Set(
                    alternates.filter(a => a.kind === 'keep').map(a => (a as Extract<AlternateSlot, { kind: 'keep' }>).uri),
                  );
                  const removed = initialAlternates.filter(a => !keptUris.has(a.uri));
                  const added = alternates.filter((a): a is Extract<AlternateSlot, { kind: 'new' }> => a.kind === 'new');

                  return (
                    <div className="rounded-lg border border-neutral-border p-4">
                      <span className="text-body-bold font-body-bold text-default-font">Additional Image Changes</span>
                      {removed.length === 0 && added.length === 0 && (
                        <p className="mt-2 text-caption font-caption text-subtext-color">
                          Order preserved; contents unchanged.
                        </p>
                      )}
                      {removed.length > 0 && (
                        <div className="mt-3">
                          <span className="text-caption-bold font-caption-bold text-error-700">
                            Removed ({removed.length})
                          </span>
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {removed.map((alt, i) => (
                              <div key={`rm-${i}`} className="relative h-16 w-16 overflow-hidden rounded-md border border-error-200 bg-error-50 opacity-70">
                                <img src={alt.displayUrl} alt="Removed" className="h-full w-full object-cover" />
                                <div className="absolute inset-0 bg-error-600/10 pointer-events-none" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {added.length > 0 && (
                        <div className="mt-3">
                          <span className="text-caption-bold font-caption-bold text-success-700">
                            Added ({added.length})
                          </span>
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {added.map((alt, i) => (
                              <div key={`add-${i}`} className="relative h-16 w-16 overflow-hidden rounded-md border border-success-200 bg-success-50">
                                <img src={alt.previewUrl} alt="Added" className="h-full w-full object-cover" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Trait diff */}
                {changes.includes('Traits') && (
                  <div className="rounded-lg border border-neutral-border p-4">
                    <span className="text-body-bold font-body-bold text-default-font">Trait Changes</span>
                    <div className="mt-2 flex flex-col gap-1">
                      {traits.filter(t => t.trait_type.trim() && t.value.trim()).map((trait, i) => {
                        const original = (nft.traits_with_rarity || nft.traits || []).find(t => t.trait_type === trait.trait_type);
                        const isNew = !original;
                        const isChanged = original && String(original.value) !== trait.value;
                        return (
                          <div key={i} className="flex items-center gap-2 text-caption font-caption">
                            <span className="text-subtext-color">{trait.trait_type}:</span>
                            {isNew ? (
                              <Badge variant="success">{trait.value} (new)</Badge>
                            ) : isChanged ? (
                              <>
                                <span className="line-through text-subtext-color">{original?.value}</span>
                                <span className="text-brand-700">{trait.value}</span>
                              </>
                            ) : (
                              <span className="text-default-font">{trait.value}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'confirm' && (
              <div className="flex flex-col items-center gap-6 py-4">
                {submitting ? (
                  <>
                    <LoadingSpinner size="lg" className="text-brand-600" />
                    <div className="text-center">
                      <p className="text-body-bold font-body-bold text-default-font">Mutating NFT...</p>
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        Uploading image, pinning metadata to IPFS, and submitting transaction.
                        This may take up to 30 seconds.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <p className="text-body-bold font-body-bold text-default-font">Ready to Mutate</p>
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        This will update the NFT's on-chain metadata URI via an NFTokenModify transaction.
                        The change is permanent and will be visible to all holders.
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {changes.map(change => (
                        <Badge key={change} variant="brand">{change}</Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 'sign' && (
              <div className="flex flex-col items-center gap-6 py-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-50">
                  <FeatherShield className="h-8 w-8 text-brand-600" />
                </div>
                <div className="text-center max-w-md">
                  <p className="text-body-bold font-body-bold text-default-font">Sign the mutation in Xaman</p>
                  <p className="text-caption font-caption text-subtext-color mt-2">
                    Because you're the issuer of this NFT, you sign the <code className="text-caption-bold font-caption-bold">NFTokenModify</code> transaction directly —
                    no AccountSet authorization needed.
                  </p>
                </div>
              </div>
            )}

            {step === 'authorize' && (
              <div className="flex flex-col items-center gap-6 py-4">
                {authorizationSuccess ? (
                  <>
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-50">
                      <FeatherCheck className="h-8 w-8 text-success-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-body-bold font-body-bold text-default-font">Minter Authorized</p>
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        Your account has been updated. You can now proceed with the mutation.
                      </p>
                    </div>
                  </>
                ) : authorizingMinter ? (
                  <>
                    <LoadingSpinner size="lg" className="text-brand-600" />
                    <div className="text-center">
                      <p className="text-body-bold font-body-bold text-default-font">Creating authorization request...</p>
                    </div>
                  </>
                ) : isIssuer ? (
                  <>
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning-50">
                      <FeatherShield className="h-8 w-8 text-warning-600" />
                    </div>
                    <div className="text-center max-w-md">
                      <p className="text-body-bold font-body-bold text-default-font">Minter Authorization Required</p>
                      <p className="text-caption font-caption text-subtext-color mt-2">
                        To mutate NFTs, your account must authorize our platform's minter wallet.
                        This is a one-time <code className="text-caption-bold font-caption-bold">AccountSet</code> transaction
                        that sets the <code className="text-caption-bold font-caption-bold">NFTokenMinter</code> field on your account.
                      </p>
                      <p className="text-caption font-caption text-subtext-color mt-2">
                        You'll be prompted to sign this transaction in Xaman.
                      </p>
                    </div>
                    <Button
                      variant="brand-primary"
                      icon={<FeatherShield className="h-4 w-4" />}
                      onClick={handleRequestAuthorization}
                    >
                      Authorize Minter
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning-50">
                      <FeatherAlertCircle className="h-8 w-8 text-warning-600" />
                    </div>
                    <div className="text-center max-w-md">
                      <p className="text-body-bold font-body-bold text-default-font">Minter Authorization Required</p>
                      <p className="text-caption font-caption text-subtext-color mt-2">
                        The collection issuer must authorize our platform's minter wallet before NFTs can be mutated.
                        Only the issuer can sign this transaction since it modifies their account.
                      </p>
                      <p className="text-caption font-caption text-subtext-color mt-2">
                        Please ask the collection issuer to authorize the minter from their account.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-neutral-border px-6 py-4">
            <div>
              {step === 'authorize' && !authorizingMinter && !authorizationSuccess && (
                <Button
                  variant="neutral-secondary"
                  icon={<FeatherChevronLeft className="h-4 w-4" />}
                  onClick={() => { setStep('confirm'); setError(null); }}
                >
                  Back
                </Button>
              )}
              {step !== 'edit' && step !== 'authorize' && step !== 'sign' && !submitting && (
                <Button
                  variant="neutral-secondary"
                  icon={<FeatherChevronLeft className="h-4 w-4" />}
                  onClick={() => setStep(step === 'confirm' ? 'preview' : 'edit')}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!isBusy && !authorizationSuccess && (
                <Button variant="neutral-tertiary" onClick={onClose}>
                  Cancel
                </Button>
              )}
              {step === 'edit' && (
                <Button
                  variant="brand-primary"
                  iconRight={<FeatherChevronRight className="h-4 w-4" />}
                  onClick={() => setStep('preview')}
                  disabled={!name.trim()}
                >
                  Preview
                </Button>
              )}
              {step === 'preview' && (
                <Button
                  variant="brand-primary"
                  iconRight={<FeatherChevronRight className="h-4 w-4" />}
                  onClick={() => setStep('confirm')}
                  disabled={!hasChanges}
                >
                  Continue
                </Button>
              )}
              {step === 'confirm' && !submitting && (
                <Button
                  variant="brand-primary"
                  icon={<FeatherCheck className="h-4 w-4" />}
                  onClick={handleSubmit}
                >
                  Mutate Now
                </Button>
              )}
              {step === 'authorize' && authorizationSuccess && (
                <Button
                  variant="brand-primary"
                  iconRight={<FeatherChevronRight className="h-4 w-4" />}
                  onClick={handleRetryAfterAuth}
                >
                  Continue to Mutate
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Xaman signing modal for minter authorization (non-issuer path) */}
      {xamanPayload && (
        <WalletSigningModal
          open={true}
          onClose={() => {
            setXamanPayload(null);
            setAuthorizingMinter(false);
          }}
          title="Authorize Minter"
          payload={xamanPayload}
          onSuccess={handleAuthorizationSuccess}
          onRejected={handleAuthorizationRejected}
          onExpired={handleAuthorizationExpired}
          message="Scan the QR code with Xaman to authorize the minter wallet on your account"
        />
      )}

      {/* Xaman signing modal for the NFTokenModify itself (issuer path) */}
      {signPayload && (
        <WalletSigningModal
          open={true}
          onClose={handleSignRejected}
          title="Sign NFTokenModify"
          payload={signPayload}
          onSuccess={handleSignSuccess}
          onRejected={handleSignRejected}
          onExpired={handleSignExpired}
          message="Scan the QR code with Xaman to sign the NFTokenModify transaction"
        />
      )}
    </>
  );
};
